import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Order, OrderStatus, OrderRefund } from '../types/ecommerce.types';
import api from '../services/api';

interface OrderDetailModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (orderId: string, newStatus: OrderStatus) => void;
  /**
   * Rembourser un article d'une commande payée, y compris déjà retirée.
   * Le bouton de la caisse (onglet Préparation) ne voit plus une commande
   * retirée : le 10/10/2026, une courge oubliée à la remise n'a pu être
   * remboursée que par un script.
   */
  onRefund?: () => void;
  /** Faire livrer la commande (adresse + jour) — commande en ligne seulement. */
  onLivrer?: () => void;
}

export default function OrderDetailModal({ order, isOpen, onClose, onStatusUpdate, onRefund, onLivrer }: OrderDetailModalProps) {
  const [orderRefunds, setOrderRefunds] = useState<OrderRefund[]>([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [lotsByProduct, setLotsByProduct] = useState<Record<string, any[]>>({});
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);

  // Charger les remboursements et la traçabilité lots quand la modal s'ouvre
  useEffect(() => {
    if (isOpen && order) {
      loadOrderRefunds();
      loadLotTraceability();
    } else {
      setOrderRefunds([]);
      setLotsByProduct({});
    }
  }, [isOpen, order]);

  const loadOrderRefunds = async () => {
    try {
      setLoadingRefunds(true);
      const refunds = await api.getRefundsForOrder(order.id);
      setOrderRefunds(refunds);
    } catch (err) {
      console.error('[OrderDetailModal] Erreur chargement remboursements:', err);
      setOrderRefunds([]);
    } finally {
      setLoadingRefunds(false);
    }
  };

  const loadLotTraceability = async () => {
    try {
      const data = await api.get(`/orders/admin/${order.id}/lots`);
      setLotsByProduct(data || {});
    } catch (err) {
      console.error('[OrderDetailModal] Erreur chargement lots:', err);
    }
  };

  // Configuration des transitions de statut autorisées
  const getNextStatuses = (currentStatus: OrderStatus): OrderStatus[] => {
    const statusFlow: Record<OrderStatus, OrderStatus[]> = {
      pending: ['confirmed', 'cancelled'],
      confirmed: ['preparing', 'cancelled'],
      preparing: ['ready', 'cancelled'],
      ready: ['delivering', 'completed', 'cancelled'],
      delivering: ['completed', 'cancelled'],
      completed: [],
      cancelled: [],
    };
    return statusFlow[currentStatus] || [];
  };

  const getStatusLabel = (status: OrderStatus): string => {
    const labels: Record<OrderStatus, string> = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready: 'Prête',
      delivering: 'En livraison',
      completed: 'Complétée',
      cancelled: 'Annulée',
    };
    return labels[status];
  };

  const getStatusIcon = (status: OrderStatus): string => {
    const icons: Record<OrderStatus, string> = {
      pending: '⏳',
      confirmed: '✅',
      preparing: '📦',
      ready: '🎉',
      delivering: '🚚',
      completed: '✔️',
      cancelled: '❌',
    };
    return icons[status];
  };

  const handlePrint = () => {
    window.print();
  };

  const nextStatuses = getNextStatuses(order.status);

  return (
    <>
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <Dialog.Title as="h3" className="text-2xl font-bold">
                        Commande {order.order_number}
                      </Dialog.Title>
                      <p className="text-sm text-emerald-100 mt-1">
                        {new Date(order.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                  {/* Statut actuel et actions */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Statut actuel</p>
                        <div className="flex items-center gap-2">
                          <span className="text-3xl">{getStatusIcon(order.status)}</span>
                          <span className="text-2xl font-bold text-gray-900">{getStatusLabel(order.status)}</span>
                        </div>
                      </div>

                      {/* Actions de changement de statut */}
                      {nextStatuses.length > 0 && (
                        <div className="flex gap-2">
                          {nextStatuses.map((status) => (
                            <button
                              key={status}
                              onClick={() => onStatusUpdate(order.id, status)}
                              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                status === 'cancelled'
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
                              }`}
                            >
                              {getStatusIcon(status)} {getStatusLabel(status)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Informations client et livraison/retrait */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span>👤</span> Client
                      </h4>
                      <dl className="space-y-2">
                        <div>
                          <dt className="text-sm text-gray-500">Nom</dt>
                          <dd className="text-sm font-medium text-gray-900">{order.customer_name}</dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500">Email</dt>
                          <dd className="text-sm font-medium text-gray-900">{order.customer_email}</dd>
                        </div>
                        {order.customer_phone && (
                          <div>
                            <dt className="text-sm text-gray-500">Téléphone</dt>
                            <dd className="text-sm font-medium text-gray-900">{order.customer_phone}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    {/* Informations livraison/retrait */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        {order.delivery_option === 'delivery' ? <span>🚚</span> : <span>🏪</span>}
                        {order.delivery_option === 'delivery' ? 'Livraison' : 'Retrait'}
                      </h4>
                      <dl className="space-y-2">
                        {order.delivery_option === 'delivery' && order.delivery_address && (
                          <>
                            <div>
                              <dt className="text-sm text-gray-500">Adresse</dt>
                              <dd className="text-sm font-medium text-gray-900">
                                {order.delivery_address.street}
                                <br />
                                {order.delivery_address.postal_code} {order.delivery_address.city}
                                {order.delivery_address.additional_info && (
                                  <>
                                    <br />
                                    <span className="text-gray-600">Note: {order.delivery_address.additional_info}</span>
                                  </>
                                )}
                              </dd>
                            </div>
                          </>
                        )}
                        {order.delivery_date && (
                          <div>
                            <dt className="text-sm text-gray-500">Date souhaitée</dt>
                            <dd className="text-sm font-medium text-gray-900">
                              {new Date(order.delivery_date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </dd>
                          </div>
                        )}
                        {order.delivery_slot && (
                          <div>
                            <dt className="text-sm text-gray-500">Créneau horaire</dt>
                            <dd className="text-sm font-medium text-gray-900">{order.delivery_slot}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>

                  {/* Historique de traçabilité (uniquement si présent) */}
                  {(order.prepared_at || order.delivered_at) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span>📋</span> Traçabilité
                      </h4>
                      <div className="space-y-3">
                        {order.prepared_at && (
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-green-500"></div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Préparation terminée</p>
                              <p className="text-sm text-gray-600">
                                {new Date(order.prepared_at).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                                {order.prepared_by && ` • Préparée par ${order.prepared_by.name}`}
                              </p>
                            </div>
                          </div>
                        )}
                        {order.delivered_at && (
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500"></div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Livraison effectuée</p>
                              <p className="text-sm text-gray-600">
                                {new Date(order.delivered_at).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                                {order.delivered_by && ` • Livrée par ${order.delivered_by}`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Articles de la commande */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span>📦</span> Articles commandés
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                              Quantité
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                              Prix unitaire
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {order.lines.map((line) => (
                            <tr key={line.id}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div>{line.product_name}</div>
                                {/* Traçabilité lots */}
                                {lotsByProduct[line.product_id] && lotsByProduct[line.product_id].length > 0 && (
                                  <div className="mt-1 space-y-1">
                                    {lotsByProduct[line.product_id].map((lot: any, li: number) => (
                                      <div key={li} className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500">
                                        <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">{lot.lot_number}</span>
                                        {lot.supplier_name && <span>{lot.supplier_name}</span>}
                                        {lot.expiry_date && (
                                          <span className="text-orange-600">
                                            DLC {new Date(lot.expiry_date).toLocaleDateString('fr-FR')}
                                          </span>
                                        )}
                                        {lot.source_document_url && (
                                          <a
                                            href={lot.source_document_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-1 py-0.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                          >
                                            BL {lot.delivery_number || ''}
                                          </a>
                                        )}
                                        {lot.label_photo_url && (
                                          <button
                                            onClick={() => setPreviewPhotoUrl(lot.label_photo_url)}
                                            className="px-1 py-0.5 bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
                                          >
                                            Photo
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-center">{line.quantity}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                {(line.unit_price_cents / 100).toFixed(2)} €
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                {(line.total_price_cents / 100).toFixed(2)} €
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Résumé financier */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span>💰</span> Résumé
                    </h4>
                    <dl className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-600">Sous-total HT</dt>
                        <dd className="font-medium text-gray-900">{(order.subtotal_cents / 100).toFixed(2)} €</dd>
                      </div>
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-600">TVA</dt>
                        <dd className="font-medium text-gray-900">{(order.tax_cents / 100).toFixed(2)} €</dd>
                      </div>
                      {order.discount_cents > 0 && (
                        <div className="flex justify-between text-sm">
                          <dt className="text-gray-600">Remise</dt>
                          <dd className="font-medium text-red-600">-{(order.discount_cents / 100).toFixed(2)} €</dd>
                        </div>
                      )}
                      {order.delivery_fee_cents > 0 && (
                        <div className="flex justify-between text-sm">
                          <dt className="text-gray-600">Frais de livraison</dt>
                          <dd className="font-medium text-gray-900">{(order.delivery_fee_cents / 100).toFixed(2)} €</dd>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2 mt-2">
                        <dt className="text-gray-900">Total TTC</dt>
                        <dd className="text-emerald-600">{(order.total_cents / 100).toFixed(2)} €</dd>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <dt className="text-gray-600">Mode de paiement</dt>
                        <dd className="font-medium text-gray-900">
                          {order.payment_method === 'wallet' && '💳 Wallet'}
                          {order.payment_method === 'card' && '💳 Carte bancaire'}
                          {order.payment_method === 'cash' && '💵 Espèces'}
                          {order.payment_method === 'mixed' && '🔀 Mixte'}
                        </dd>
                      </div>
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-600">Statut paiement</dt>
                        <dd
                          className={`font-medium ${
                            order.payment_status === 'paid'
                              ? 'text-green-600'
                              : order.payment_status === 'pending'
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {order.payment_status === 'paid' && '✅ Payé'}
                          {order.payment_status === 'pending' && '⏳ En attente'}
                          {order.payment_status === 'failed' && '❌ Échoué'}
                          {order.payment_status === 'refunded' && '↩️ Remboursé'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Remboursements */}
                  {loadingRefunds ? (
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-gray-600">Chargement des remboursements...</div>
                    </div>
                  ) : orderRefunds.length > 0 && (
                    <div className="bg-white border border-orange-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-orange-700 mb-4 flex items-center gap-2">
                        <span>💰</span> Remboursements
                      </h4>
                      <div className="space-y-3">
                        {orderRefunds.map((refund) => (
                          <div
                            key={refund.id}
                            className={`p-3 rounded-lg border-2 ${
                              refund.status === 'approved'
                                ? 'bg-green-50 border-green-300'
                                : refund.status === 'rejected'
                                ? 'bg-red-50 border-red-300'
                                : 'bg-yellow-50 border-yellow-300'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <div className="font-bold text-base">
                                  {refund.refund_number}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {new Date(refund.requested_at).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold text-green-600">
                                  {(refund.amount_cents / 100).toFixed(2)} €
                                </div>
                                <div className={`text-xs font-bold ${
                                  refund.status === 'approved'
                                    ? 'text-green-700'
                                    : refund.status === 'rejected'
                                    ? 'text-red-700'
                                    : 'text-yellow-700'
                                }`}>
                                  {refund.status === 'approved' && '✓ Remboursé'}
                                  {refund.status === 'rejected' && '✕ Rejeté'}
                                  {refund.status === 'pending' && '⏳ En attente'}
                                  {refund.status === 'completed' && '✓ Finalisé'}
                                </div>
                              </div>
                            </div>

                            <div className="space-y-1 text-xs">
                              <div className="flex items-start">
                                <span className="font-medium text-gray-700 min-w-[80px]">Type :</span>
                                <span className="text-gray-900">
                                  {refund.refund_type === 'product_missing' && 'Produit manquant'}
                                  {refund.refund_type === 'product_defective' && 'Produit défectueux'}
                                  {refund.refund_type === 'weight_difference' && 'Différence de poids'}
                                  {refund.refund_type === 'order_cancelled' && 'Commande annulée'}
                                  {refund.refund_type === 'goodwill_gesture' && 'Geste commercial'}
                                </span>
                              </div>
                              <div className="flex items-start">
                                <span className="font-medium text-gray-700 min-w-[80px]">Motif :</span>
                                <span className="text-gray-900">{refund.reason}</span>
                              </div>
                              {refund.product_name && (
                                <div className="flex items-start">
                                  <span className="font-medium text-gray-700 min-w-[80px]">Produit :</span>
                                  <span className="text-gray-900">
                                    {refund.product_name}
                                    {refund.quantity_affected && ` (Qté: ${refund.quantity_affected})`}
                                  </span>
                                </div>
                              )}
                              {refund.description && (
                                <div className="flex items-start">
                                  <span className="font-medium text-gray-700 min-w-[80px]">Description :</span>
                                  <span className="text-gray-900 italic">{refund.description}</span>
                                </div>
                              )}
                              {refund.approved_at && (
                                <div className="flex items-start mt-2 pt-2 border-t border-gray-300">
                                  <span className="font-medium text-gray-700 min-w-[80px]">Approuvé le :</span>
                                  <span className="text-gray-900">
                                    {new Date(refund.approved_at).toLocaleDateString('fr-FR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>

                            {refund.status === 'approved' && (
                              <div className="mt-2 p-2 bg-white rounded border border-green-200 text-xs text-green-800">
                                <strong>✓ Montant crédité sur le compte prépayé du client</strong>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Total des remboursements */}
                      {orderRefunds.filter(r => r.status === 'approved').length > 0 && (
                        <div className="mt-3 p-3 bg-gradient-to-r from-green-100 to-green-50 rounded-lg border-2 border-green-400">
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-gray-800 text-sm">Total remboursé :</span>
                            <span className="text-xl font-bold text-green-700">
                              {(orderRefunds
                                .filter(r => r.status === 'approved')
                                .reduce((sum, r) => sum + r.amount_cents, 0) / 100
                              ).toFixed(2)} €
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Crédité sur le compte prépayé du client
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {order.notes && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                        <span>📝</span> Notes de la commande
                      </h4>
                      <p className="text-sm text-yellow-800">{order.notes}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                      />
                    </svg>
                    Imprimer bon de préparation
                  </button>

                  {/* Commande en ligne PAYÉE seulement : elle ne se paie pas à la porte. */}
                  {onLivrer && order.customer_id && !['caisse', 'enfact_bl'].includes((order as any).order_type)
                    // Payée, ou « à régler » : le livreur l'encaissera à la porte (21/10/2026).
                    && (order.payment_status === 'paid' || (order as any).payment_method === 'cash')
                    && !['completed', 'cancelled', 'delivering'].includes(order.status as string) && (
                    <button
                      onClick={onLivrer}
                      className="px-4 py-2 bg-sky-50 border border-sky-300 text-sky-800 rounded-lg hover:bg-sky-100 transition-colors font-semibold"
                    >
                      🚚 {order.delivery_option === 'delivery' ? 'Modifier la livraison' : 'Livrer cette commande'}
                    </button>
                  )}

                  {onRefund && ['paid', 'partially_refunded'].includes(order.payment_status as string) && (
                    <button
                      onClick={onRefund}
                      className="px-4 py-2 bg-orange-50 border border-orange-300 text-orange-800 rounded-lg hover:bg-orange-100 transition-colors font-semibold"
                    >
                      💰 Rembourser un article
                    </button>
                  )}

                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Preview photo étiquette */}
    {previewPhotoUrl && (
      <div
        className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
        onClick={() => setPreviewPhotoUrl(null)}
      >
        <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setPreviewPhotoUrl(null)}
            className="absolute -top-10 right-0 text-white hover:text-gray-300 text-sm"
          >
            Fermer
          </button>
          <img src={previewPhotoUrl} alt="Photo etiquette" className="w-full rounded-lg shadow-2xl" />
        </div>
      </div>
    )}
    </>
  );
}
